import { create } from 'zustand';
import { fetchAndActivate, getAll, getValue, Value } from 'firebase/remote-config';
import { ApiUtils, type StoreBaseReturn } from '@shared/api';
import { remoteConfig } from '../../../firebase.config';

enum ValueEnum {
  Boolean = 'boolean',
  Number = 'number',
  JSON = 'json',
  String = 'string',
}
type ValueType = ValueEnum | `${ValueEnum}`;

interface UseRemoteConfigStore {
  loading: boolean;
  data: Record<string, Value>;
  error?: string | null;
  getRemoteConfig: () => Promise<StoreBaseReturn<Record<string, Value>>>;
  getValue: <T = string>(key: string, type?: ValueType) => T;
  clear: () => void;
}

export const useRemoteConfigStore = create<UseRemoteConfigStore>((set) => ({
  loading: false,
  data: {},
  error: null,

  getRemoteConfig: async () => {
    try {
      set({ loading: true, error: null });
      await fetchAndActivate(remoteConfig);
      const data = getAll(remoteConfig);
      set({ loading: false, data });
      return { loading: false, data };
    } catch (error) {
      set({ loading: false, data: {}, error: ApiUtils.getApiError(error) });
      return { loading: false, data: {}, error: ApiUtils.getApiError(error) };
    }
  },

  getValue: (key, type) => {
    const value: Value = getValue(remoteConfig, key);

    switch (type) {
      case ValueEnum.String:
        return value.asString();
      case ValueEnum.Number:
        return value.asNumber();
      case ValueEnum.Boolean:
        return value.asBoolean();
      case ValueEnum.JSON:
        return JSON.parse(value.asString());
      default:
        return value.asString();
    }
  },

  clear: () => set({ loading: false, data: {}, error: null }),
}));
